/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/IncompleteSubmissions/IncompleteSubmissions.tsx.
 */

// src/pages/Dashboard/Contributor/IncompleteSubmissions/IncompleteSubmissions.tsx
import React, { useEffect, useState, useMemo } from "react";
import "../Contributor.css";
import "./IncompleteSubmissions.css";
import axiosClient from "../../../../api/axiosClient";
import { getMySubmissions } from "../../../../services/submissionService";
import DataTable, { Column } from "../../../../components/common/DataTable";
import AnalysisBanner from "../../../../components/common/AnalysisBanner";

/** Type definition — Resume Submission Choice. */
export type ResumeSubmissionChoice = "beginning" | "current";

interface IncompleteSubmissionsProps {
  onResumeSubmission: (
    id: string,
    options?: { mode: ResumeSubmissionChoice; currentStage?: string }
  ) => void;
}

/* ===========================================================
   PROFESSIONAL CONFIRM DELETE MODAL
=========================================================== */
const ConfirmDeleteModal: React.FC<{
  open: boolean;
  title?: string;
  message?: string;
  onCancel: () => void;
  onConfirm: () => void;
  loading?: boolean;
}> = ({ open, title = "Delete Submission", message, onCancel, onConfirm, loading }) => {
  if (!open) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="modal-card fade-in">

        {/* HEADER */}
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onCancel}>×</button>
        </div>

        {/* CONTENT */}
        <div className="modal-body">
          <p>{message ?? "Are you sure you want to delete this submission?"}</p>
        </div>

        {/* FOOTER */}
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
          <button className="btn-danger" onClick={onConfirm} disabled={loading}>
            {loading ? "Deleting…" : "Delete"}
          </button>
        </div>

      </div>
    </div>
  );
};

/** Choose whether to jump to saved stage or restart the wizard */
const ResumeSubmissionModal: React.FC<{
  open: boolean;
  submissionCode?: string;
  /** Raw API value e.g. RAW_MATERIAL_EXTRACTION_A1 */
  apiCurrentStage?: string;
  /** Human-readable stage for display */
  currentStageLabel?: string;
  onClose: () => void;
  onChoose: (mode: ResumeSubmissionChoice) => void;
}> = ({
  open,
  submissionCode,
  apiCurrentStage,
  currentStageLabel,
  onClose,
  onChoose,
}) => {
  if (!open) return null;

  const stageSummary =
    currentStageLabel && currentStageLabel !== "-"
      ? currentStageLabel
      : apiCurrentStage
        ? apiCurrentStage.replace(/_/g, " ").replace(/A1A3/gi, "A1-A3")
        : null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="modal-card fade-in">
        <div className="modal-header">
          <h2>Resume submission</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="modal-body">
          {submissionCode ? (
            <p style={{ marginTop: 0 }}>
              <strong>{submissionCode}</strong>
            </p>
          ) : null}
          {apiCurrentStage || stageSummary ? (
            <div className="resume-modal-current-stage">
              <div className="resume-modal-current-stage-label">Current stage</div>
              {stageSummary ? (
                <div className="resume-modal-current-stage-title">{stageSummary}</div>
              ) : null}
              {apiCurrentStage ? (
                <code className="resume-modal-current-stage-code">{apiCurrentStage}</code>
              ) : null}
            </div>
          ) : (
            <p className="resume-modal-current-stage-missing">
              No current stage was returned for this submission. Use &quot;Start from beginning&quot; or
              try again later.
            </p>
          )}
          <p>
            Open the step you were last working on, or start again from the first step (Contributor&apos;s
            Details).
          </p>
        </div>
        <div className="modal-footer resume-submission-modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => onChoose("beginning")}
          >
            Start from beginning
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={() => onChoose("current")}
            disabled={!apiCurrentStage && !stageSummary}
          >
            {stageSummary
              ? `Open current stage (${stageSummary})`
              : "Open current stage"}
          </button>
        </div>
      </div>
    </div>
  );
};

const IncompleteSubmissions: React.FC<IncompleteSubmissionsProps> = ({
  onResumeSubmission,
}) => {
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState("");

  // Resume wizard modal
  const [resumeModalOpen, setResumeModalOpen] = useState(false);
  const [resumeRow, setResumeRow] = useState<{
    id: string;
    submissionCode?: string;
    apiCurrentStage?: string;
  } | null>(null);

  // Modal + delete UI states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deletingLoading, setDeletingLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Success banner
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  /* LOAD SUBMISSIONS */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await getMySubmissions();
        if (res.data.success) setSubmissions(res.data.data || []);
      } catch (err) {
        console.error("❌ Failed loading submissions", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* 🔍 Format date helper */
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  /* 🔍 Format stage for display only (API values unchanged elsewhere) */
  const formatStage = (stage: string) => {
    if (!stage) return "-";
    return stage.replace(/_/g, " ").replace(/A1A3/gi, "A1-A3");
  };

  // Filter submissions based on search term
  const filteredSubmissions = useMemo(() => {
    if (!search.trim()) {
      return submissions;
    }
    const searchLower = search.toLowerCase().trim();
    return submissions.filter((submission) => {
      // Search by submission code
      const code = submission.submissionCode?.toLowerCase() || "";
      // Also search by formatted date and stage
      const date = formatDate(submission.createdAt).toLowerCase();
      const stageRaw =
        submission.currentStage ?? submission.current_stage ?? "";
      const stage = formatStage(stageRaw).toLowerCase();
      return code.includes(searchLower) || date.includes(searchLower) || stage.includes(searchLower);
    });
  }, [submissions, search]);

  // Column definitions
  const columns: Column<any>[] = useMemo(
    () => [
      {
        key: "submissionCode",
        label: "Submission Code",
      },
      {
        key: "createdAt",
        label: "Created",
        render: (item: any) => formatDate(item.createdAt),
      },
      {
        key: "currentStage",
        label: "Current Stage",
        render: (item: any) =>
          formatStage(item.currentStage ?? item.current_stage ?? ""),
      },
    ],
    []
  );

  const openResumeModal = (item: any) => {
    const apiCurrentStage =
      item.currentStage ?? item.current_stage ?? item.stage ?? "";
    setResumeRow({
      id: String(item.id),
      submissionCode: item.submissionCode ?? item.submission_code,
      apiCurrentStage: apiCurrentStage || undefined,
    });
    setResumeModalOpen(true);
  };

  const closeResumeModal = () => {
    setResumeModalOpen(false);
    setResumeRow(null);
  };

  const confirmResume = (mode: ResumeSubmissionChoice) => {
    if (!resumeRow) return;
    onResumeSubmission(resumeRow.id, {
      mode,
      currentStage: resumeRow.apiCurrentStage,
    });
    closeResumeModal();
  };

  // Render actions
  const renderActions = (item: any) => (
    <div className="action-buttons">
      <button
        className="action-btn-icon edit-btn"
        title="Resume submission"
        onClick={() => openResumeModal(item)}
      >
        ✏️
      </button>
      <button
        className="action-btn-icon delete-btn"
        title="Delete Submission"
        onClick={() => openDeleteModal(item.id)}
      >
        🗑️
      </button>
    </div>
  );

  /* OPEN DELETE MODAL */
  const openDeleteModal = (id: string) => {
    setDeletingId(id);
    setErrorMessage(null);
    setDeleteModalOpen(true);
  };

  /* CLOSE DELETE MODAL */
  const closeDeleteModal = () => {
    setDeletingId(null);
    setErrorMessage(null);
    setDeleteModalOpen(false);
    setDeletingLoading(false);
  };

  /* DELETE HANDLER */
  const handleDelete = async () => {
    if (!deletingId) return;

    setDeletingLoading(true);
    setErrorMessage(null);

    try {
      const res = await axiosClient.delete(`/submissions/${deletingId}`);

      if (res.data?.success) {
        // Remove from UI list
        setSubmissions((prev) => prev.filter((s) => s.id !== deletingId));

        closeDeleteModal();

        // Show success banner
        setSuccessMessage("Submission deleted successfully.");
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setErrorMessage(res.data?.message || "Delete failed");
      }
    } catch (err: any) {
      console.error("Delete failed:", err);
      setErrorMessage(
        err?.response?.data?.message || err.message || "Delete failed"
      );
    } finally {
      setDeletingLoading(false);
    }
  };

  return (
    <div className="incomplete-submissions-container">
      {/* Header Section */}
      <AnalysisBanner
        title="Incomplete Submissions"
      />

      {/* SUCCESS MESSAGE */}
      {successMessage && (
        <div className="success-banner fade-in">
          ✅ {successMessage}
        </div>
      )}

      {/* Submission List */}
      <div className="submission-list-section">
        <DataTable
          data={filteredSubmissions}
          columns={columns}
          loading={loading}
          searchPlaceholder="Search by Submission Code..."
          onSearch={(searchTerm) => setSearch(searchTerm)}
          searchValue={search}
          pagination={true}
          itemsPerPage={10}
          renderActions={renderActions}
          emptyMessage="No incomplete submissions found. Start by creating a new submission."
        />
      </div>

      <ResumeSubmissionModal
        open={resumeModalOpen}
        submissionCode={resumeRow?.submissionCode}
        apiCurrentStage={resumeRow?.apiCurrentStage}
        currentStageLabel={
          resumeRow?.apiCurrentStage
            ? formatStage(resumeRow.apiCurrentStage)
            : undefined
        }
        onClose={closeResumeModal}
        onChoose={confirmResume}
      />

      {/* DELETE MODAL */}
      <ConfirmDeleteModal
        open={deleteModalOpen}
        title="Delete Submission?"
        message="This action will permanently delete your submission and all associated data. This cannot be undone."
        onCancel={closeDeleteModal}
        onConfirm={handleDelete}
        loading={deletingLoading}
      />

      {/* ERROR BANNER */}
      {errorMessage && (
        <div className="submission-delete-error">
          <strong>Error:</strong> {errorMessage}
        </div>
      )}
    </div>
  );
};

/** React component — Incomplete Submissions. */
export default IncompleteSubmissions;

